import { AsyncPipe, NgClass } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Inject,
  Input,
  Output,
  ViewEncapsulation,
} from '@angular/core';
import { FormGroup, ReactiveFormsModule } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import {
  CLIENT_APP_ID,
  ClientAppId,
  LocationData,
  LocationDataGeocoded,
} from '@sc/data-access-models';
import { TranslatorService } from '@sc/data-access-translation';
import { FeatureFlippingManagerService } from '@sc/feature-flipping';
import { Observable, combineLatest } from 'rxjs';
import { map } from 'rxjs/operators';
import { ButtonComponent } from '../button';
import { InputTextComponent } from '../input-text';

@Component({
  selector: 'sc-address-search-bar',
  styleUrls: ['./address-search-bar.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  imports: [
    NgClass,
    ReactiveFormsModule,
    InputTextComponent,
    ButtonComponent,
    TranslateModule,
    AsyncPipe,
  ],
  template: `
    <div class="search-address-container" [formGroup]="form">
      <sc-input-text
        tourAnchor="intervention-search-bar"
        formControlName="address"
        autocomplete="street-address"
        [label]="
          'CustomerDeclaration.AppointmentSearchPage.searchAddressInput'
            | translate
        "
        leftIcon="map_location"
        [hint]="getHintText() | async"
        [suggestions]="suggestedLocationsData"
        suggestionsLabelName="address"
        [forceHighlightRequired]="false"
        (updateSuggestions)="onUpdateAddressSuggestions($event)"
        (selectSuggestion)="onSelectAddress($event)"
        [attr.data-testid]="'searchInput'"
      ></sc-input-text>
      <sc-button
        id="searchButton"
        [ngClass]="{ hint: !!(getHintText() | async) }"
        (click)="onSearchButtonClick()"
        leftIcon="search"
        [loading]="isSearchButtonLoadingState()"
        [disabled]="!form.valid || isSearchButtonLoadingState()"
      >
        {{
          'CustomerDeclaration.AppointmentSearchPage.searchAddressButton'
            | translate
        }}
      </sc-button>
    </div>
  `,
})
export class AddressSearchBarComponent {
  @Input() public selectedSearchLocationGeoCodedData: LocationDataGeocoded;
  @Input() public form: FormGroup;
  @Input() public hasOutsideInterventionFeature: boolean;
  @Input() public suggestedLocationsData: LocationData[] = [];
  @Input() public loading: boolean;

  @Output() public selectedAddressEvent = new EventEmitter<LocationData>();
  @Output() public updateAddressSuggestionsEvent = new EventEmitter<string>();
  @Output() public searchClickEvent = new EventEmitter<boolean>();

  constructor(
    public changeDetectorRef: ChangeDetectorRef,
    public featureFlippingService: FeatureFlippingManagerService,
    public translateService: TranslatorService,
    @Inject(CLIENT_APP_ID) private readonly clientAppId: ClientAppId
  ) {}

  onSelectAddress(locationData: LocationData) {
    this.selectedAddressEvent.emit(locationData);
  }

  onUpdateAddressSuggestions(value: string) {
    this.updateAddressSuggestionsEvent.emit(value);
  }

  onSearchButtonClick() {
    if (!this.isSearchButtonLoadingState()) {
      if (
        this.selectedSearchLocationGeoCodedData?.address !==
        this.form?.get('address')?.value
      ) {
        const value =
          this.suggestedLocationsData?.[0] ||
          this.selectedSearchLocationGeoCodedData;
        this.form?.get('address')?.setValue(value.address);
        this.onSelectAddress(value);
      } else {
        this.searchClickEvent.emit(true);
      }
    }
  }

  isSearchButtonLoadingState() {
    return this.loading;
  }

  getHintText(): Observable<string> {
    return combineLatest([
      this.translateService.getTranslation(
        'CustomerDeclaration.AppointmentSearchPage.addressFixedHint',
        {
          clientAppId: this.clientAppId,
        }
      ),
      this.translateService.getTranslation(
        'CustomerDeclaration.AppointmentSearchPage.completeAddressRequired'
      ),
    ]).pipe(
      map(([fixedHintText, completeAddrText]) => {
        const usedCompleteAddrText =
          this.selectedSearchLocationGeoCodedData?.isCompleteAddress ===
            false && this.hasOutsideInterventionFeature
            ? completeAddrText
            : '';
        const usedFixedHintText = fixedHintText;

        return [usedCompleteAddrText, usedFixedHintText]
          .filter((text) => !!text)
          .join('<br/>');
      })
    );
  }
}
